import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiGet } from '../utils/api';

function fmtMoney(n) {
  if (!n) return '—';
  return `$${n.toLocaleString()}`;
}
function fmtK(n) {
  if (!n) return '—';
  return `$${Math.round(n / 1000)}k`;
}

const FOLLOW_UP_TEMPLATES = [
  {
    step: 1, channel: 'Email', label: 'Initial Outreach',
    icon: '✉️',
    subject: 'Off-Market Deal: {address} — {neighborhood} — {beds}bd/{baths}ba — ARV {arv}',
    body: `Hi {buyer_name},

I have an off-market opportunity I think fits your buy box perfectly.

📍 {address}, {neighborhood}, Austin TX {zip}
🏠 {beds} bed / {baths} bath / {sqft} sqft — Built {year_built}
💰 Asking: {max_offer} | ARV: {arv} | Repair Est: {repairs}
📊 AI Deal Score: {score}/100

The numbers work well for a {strategy} play in this market. I've attached the full deal packet with comps, repair scope, and investor ROI projections.

Let me know if you'd like to schedule a walkthrough — I can be flexible this week.

Best,
[Your Name]
feebe`
  },
  {
    step: 2, channel: 'Text/SMS', label: '24-Hour Follow-Up',
    icon: '💬',
    subject: null,
    body: `Hey {buyer_name} — following up on {address} in {neighborhood}. Sent you the deal packet yesterday. This one is getting traction — I have 2 other investors reviewing it. ARV is solid at {arv} with {repairs} in repairs. Want to jump on a quick 10-min call today? — [Your Name]`
  },
  {
    step: 3, channel: 'Email', label: 'Final Deadline Email',
    icon: '🔔',
    subject: 'FINAL: {address} — Best & Final Offers Due {deadline}',
    body: `Hi {buyer_name},

Last note on {address} in {neighborhood}.

I'm wrapping up showings and collecting best & final offers by {deadline} at 5pm.

Quick summary:
• Address: {address}, Austin TX
• ARV: {arv} | Max Offer: {max_offer}
• Repairs: {repairs} (scope attached)
• Assignment Fee: {assign_fee}

If this deal fits your buy box, now's the time. Reply with your number and I'll get docs over immediately.

After {deadline} I can't guarantee availability.

— [Your Name]
feebe | Austin, TX`
  }
];

export default function DealPackets() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [deal, setDeal] = useState(null);
  const [underwriting, setUnderwriting] = useState(null);
  const [followUps, setFollowUps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('packet');

  useEffect(() => {
    Promise.all([
      fetch(`/api/deals/${id}`).then(r => r.json()),
      fetch(`/api/deals/${id}/follow-ups`).then(r => r.json()),
    ]).then(([dealData, fuData]) => {
      setDeal(dealData);
      setFollowUps(Array.isArray(fuData) ? fuData : []);
    }).catch(() => {}).finally(() => setLoading(false));
    // Underwriting is entitlement-gated — fetch separately so a 402 doesn't reject the whole load.
    // On a wall, underwriting stays null and the packet renders without the comp summary (guarded).
    apiGet(`/api/underwriting/${id}`).then(setUnderwriting).catch(() => {});
  }, [id]);

  if (loading) return <LoadingScreen />;
  if (!deal) return <div style={{ color: 'var(--gray-500)', padding: 40 }}>Deal not found.</div>;

  const uw = underwriting || {};
  const deadline = new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  const fillTemplate = (text) => {
    if (!text) return text;
    return text
      .replace(/{address}/g, deal.address)
      .replace(/{neighborhood}/g, deal.neighborhood)
      .replace(/{beds}/g, deal.beds)
      .replace(/{baths}/g, deal.baths)
      .replace(/{sqft}/g, deal.sqft?.toLocaleString())
      .replace(/{year_built}/g, deal.year_built)
      .replace(/{zip}/g, deal.zip)
      .replace(/{arv}/g, fmtK(deal.ai_arv))
      .replace(/{max_offer}/g, fmtK(deal.max_offer))
      .replace(/{repairs}/g, fmtK(deal.repair_cost))
      .replace(/{assign_fee}/g, fmtK(deal.assignment_fee))
      .replace(/{score}/g, deal.ai_deal_score)
      .replace(/{strategy}/g, 'Fix & Flip')
      .replace(/{deadline}/g, deadline)
      .replace(/{buyer_name}/g, '[Buyer Name]');
  };

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <button onClick={() => navigate('/pipeline')} style={{ background: 'none', border: 'none', color: 'var(--gray-500)', fontSize: 13, cursor: 'pointer', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 6 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg>
          Back to Pipeline
        </button>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--white)', marginBottom: 4, letterSpacing: '-0.02em' }}>Deal Packet</h1>
            <div style={{ fontSize: 14, color: 'var(--gray-500)' }}>{deal.address} · {deal.neighborhood} · Auto-generated investor package</div>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button style={tabBtnStyle(activeTab === 'packet')} onClick={() => setActiveTab('packet')}>Deal Packet</button>
            <button style={tabBtnStyle(activeTab === 'followup')} onClick={() => setActiveTab('followup')}>Follow-Up Sequence</button>
          </div>
        </div>
      </div>

      {activeTab === 'packet' && (
        <div>
          {/* Packet Header */}
          <div style={{
            background: 'linear-gradient(135deg, var(--navy-card) 0%, rgba(20, 80, 74,0.08) 100%)',
            border: '1px solid rgba(20, 80, 74,0.2)',
            borderRadius: 12, padding: '28px 32px', marginBottom: 20,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 20 }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--teal)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>
                  Off-Market Wholesale Opportunity
                </div>
                <h2 style={{ fontSize: 26, fontWeight: 800, color: 'var(--white)', marginBottom: 4, letterSpacing: '-0.02em' }}>{deal.address}</h2>
                <div style={{ fontSize: 16, color: 'var(--gray-300)', marginBottom: 12 }}>{deal.neighborhood}, Austin TX {deal.zip}</div>
                <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                  <Spec label="Beds" value={deal.beds} />
                  <Spec label="Baths" value={deal.baths} />
                  <Spec label="Sqft" value={deal.sqft?.toLocaleString()} />
                  <Spec label="Year Built" value={deal.year_built} />
                  <Spec label="Type" value={deal.property_type} />
                  <Spec label="Lot" value={deal.lot_size} />
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 11, color: 'var(--gray-500)', marginBottom: 4 }}>AI Deal Score</div>
                <div style={{ fontSize: 52, fontWeight: 800, color: deal.ai_deal_score >= 85 ? '#4A7C59' : '#E0A526', lineHeight: 1 }}>{deal.ai_deal_score}</div>
                <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 2 }}>/ 100</div>
              </div>
            </div>
          </div>

          {/* Description */}
          {deal.description && (
            <div style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 12, padding: '20px 24px', marginBottom: 20 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--gray-500)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Property Overview</div>
              <p style={{ fontSize: 14, color: 'var(--gray-100)', lineHeight: 1.7 }}>{deal.description}</p>
            </div>
          )}

          {/* Numbers Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 20 }}>
            <NumberCard label="Estimated ARV" value={fmtMoney(deal.ai_arv)} color="var(--white)" bg="var(--navy-card)" sub="Based on comparable sales" />
            <NumberCard label="Max Offer Price" value={fmtMoney(deal.max_offer)} color="var(--teal)" bg="rgba(20, 80, 74,0.06)" sub="Recommended acquisition price" />
            <NumberCard label="Assignment Fee" value={fmtMoney(deal.assignment_fee)} color="var(--gold)" bg="rgba(224, 165, 38,0.06)" sub="Your wholesale profit" />
            <NumberCard label="Est. Repair Cost" value={fmtMoney(deal.repair_cost)} color="var(--gray-300)" bg="var(--navy-card)" sub="Contractor estimate range" />
            <NumberCard label="Asking Price" value={fmtMoney(deal.asking_price)} color="var(--gray-300)" bg="var(--navy-card)" sub={`${Math.round(((deal.asking_price - deal.max_offer) / deal.asking_price) * 100)}% above max offer`} />
            <NumberCard label="Days on Market" value={deal.days_on_market} color="var(--gray-300)" bg="var(--navy-card)" sub={`MLS ${deal.mls_id}`} />
          </div>

          {/* Comp Summary */}
          {uw.comps && uw.comps.length > 0 && (
            <div style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 12, padding: '20px 24px', marginBottom: 20 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--gray-500)', marginBottom: 16, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Comparable Sales — Last 90 Days</div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--navy-border)' }}>
                      {['Address', 'Sold Price', 'Sqft', 'Beds/Bath', 'Dist.', 'Days Ago', '$/sqft'].map(h => (
                        <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, color: 'var(--gray-500)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {uw.comps.map((c, i) => (
                      <tr key={i} style={{ borderBottom: i < uw.comps.length - 1 ? '1px solid rgba(232, 227, 218, 0.7)' : 'none' }}>
                        <td style={{ padding: '10px 12px', fontSize: 13, color: 'var(--gray-300)', fontWeight: 500 }}>{c.address}</td>
                        <td style={{ padding: '10px 12px', fontSize: 13, color: 'var(--white)', fontWeight: 600 }}>{fmtMoney(c.sold_price)}</td>
                        <td style={{ padding: '10px 12px', fontSize: 12, color: 'var(--gray-500)' }}>{c.sqft?.toLocaleString()}</td>
                        <td style={{ padding: '10px 12px', fontSize: 12, color: 'var(--gray-500)' }}>{c.beds}/{c.baths}</td>
                        <td style={{ padding: '10px 12px', fontSize: 12, color: 'var(--gray-500)' }}>{c.distance_miles}mi</td>
                        <td style={{ padding: '10px 12px', fontSize: 12, color: 'var(--gray-500)' }}>{c.days_ago}d</td>
                        <td style={{ padding: '10px 12px', fontSize: 12, color: 'var(--teal)', fontWeight: 600 }}>${c.price_per_sqft}/sqft</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ marginTop: 12, padding: '10px 14px', background: 'rgba(20, 80, 74,0.06)', borderRadius: 8, fontSize: 12, color: 'var(--gray-300)' }}>
                <strong style={{ color: 'var(--teal)' }}>ARV Conclusion: </strong>
                Based on {uw.comps.length} comparable sales averaging ${Math.round(uw.comps.reduce((s, c) => s + c.price_per_sqft, 0) / uw.comps.length)}/sqft, AI projects ARV of <strong>{fmtMoney(deal.ai_arv)}</strong> with {uw.arvAnalysis?.confidence || 'Medium'} confidence.
              </div>
            </div>
          )}

          {/* Investor Returns */}
          {uw.roiProjections && (
            <div style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 12, padding: '20px 24px', marginBottom: 20 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--gray-500)', marginBottom: 16, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Investor Return Summary</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 10 }}>Fix & Flip Strategy</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <ReturnRow label="Purchase Price" value={fmtMoney(deal.max_offer)} />
                    <ReturnRow label="Repair Budget" value={fmtMoney(deal.repair_cost)} />
                    <ReturnRow label="All-In Cost" value={fmtMoney(uw.roiProjections.allInCost)} />
                    <ReturnRow label="After Repair Value" value={fmtMoney(deal.ai_arv)} bold />
                    <ReturnRow label="Net Profit" value={fmtMoney(uw.roiProjections.flipProfit)} color="#4A7C59" bold />
                    <ReturnRow label="ROI" value={`${uw.roiProjections.flipROI}%`} color="#4A7C59" bold />
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 10 }}>Buy & Hold / BRRRR</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <ReturnRow label="Est. Monthly Rent" value={fmtMoney(uw.roiProjections.estimatedRent)} />
                    <ReturnRow label="Monthly NOI" value={fmtMoney(uw.roiProjections.monthlyNOI)} />
                    <ReturnRow label="75% LTV Refi" value={fmtMoney(uw.roiProjections.refinanceValue)} />
                    <ReturnRow label="Cash-on-Cash" value={`${uw.roiProjections.cashOnCash}%`} color="var(--teal)" bold />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Print / Share buttons */}
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
            <button style={actionBtnStyle('var(--teal)')} onClick={() => navigate(`/buyer-match/${id}`)}>
              Match Buyers & Send →
            </button>
            <button style={actionBtnStyle('var(--gray-500)')} onClick={() => setActiveTab('followup')}>
              View Follow-Up Sequence →
            </button>
          </div>
        </div>
      )}

      {activeTab === 'followup' && (
        <div>
          <div style={{ marginBottom: 24 }}>
            <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--white)', marginBottom: 4 }}>Auto Follow-Up Sequence</h2>
            <p style={{ fontSize: 13, color: 'var(--gray-500)' }}>3-step outreach sequence for each matched buyer. Customize and activate to deploy automatically.</p>
          </div>

          {/* Sequence timeline */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0, marginBottom: 32 }}>
            {FOLLOW_UP_TEMPLATES.map((template, idx) => {
              const existingFu = followUps.find(f => f.step_number === template.step);
              const status = existingFu?.status || 'Pending';
              return (
                <FollowUpStep
                  key={template.step}
                  template={template}
                  status={status}
                  fillTemplate={fillTemplate}
                  isLast={idx === FOLLOW_UP_TEMPLATES.length - 1}
                  existingFu={existingFu}
                />
              );
            })}
          </div>

          {/* Existing tracked follow-ups */}
          {followUps.length > 0 && (
            <div style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 12, padding: '20px 24px' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--gray-500)', marginBottom: 16, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Tracked Follow-Ups for This Deal</div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--navy-border)' }}>
                    {['Step', 'Channel', 'Buyer', 'Status'].map(h => (
                      <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, color: 'var(--gray-500)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {followUps.map((fu, i) => (
                    <tr key={fu.id} style={{ borderBottom: i < followUps.length - 1 ? '1px solid rgba(232, 227, 218, 0.7)' : 'none' }}>
                      <td style={{ padding: '10px 12px', fontSize: 13, color: 'var(--gray-300)' }}>Step {fu.step_number}</td>
                      <td style={{ padding: '10px 12px', fontSize: 12, color: 'var(--gray-500)' }}>{fu.channel}</td>
                      <td style={{ padding: '10px 12px', fontSize: 13, color: 'var(--white)' }}>{fu.buyer_name} <span style={{ color: 'var(--gray-500)' }}>· {fu.buyer_company}</span></td>
                      <td style={{ padding: '10px 12px' }}>
                        <span style={{
                          fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 12,
                          background: fu.status === 'Sent' ? 'rgba(20, 80, 74,0.15)' : 'rgba(224, 165, 38,0.12)',
                          color: fu.status === 'Sent' ? 'var(--teal)' : 'var(--gold)',
                        }}>{fu.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function FollowUpStep({ template, status, fillTemplate, isLast, existingFu }) {
  const [expanded, setExpanded] = useState(template.step === 1);
  const statusColors = { 'Sent': '#4A7C59', 'Pending': '#E0A526', 'Skipped': '#B85450' };
  const sc = statusColors[status] || '#6B6760';

  return (
    <div style={{ display: 'flex', gap: 0 }}>
      {/* Timeline line */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginRight: 20 }}>
        <div style={{
          width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
          background: status === 'Sent' ? 'rgba(74, 124, 89,0.15)' : 'var(--navy-card)',
          border: `2px solid ${sc}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 14, zIndex: 1,
        }}>
          {status === 'Sent' ? '✓' : template.step}
        </div>
        {!isLast && <div style={{ width: 2, flex: 1, background: 'var(--navy-border)', minHeight: 20, marginTop: 4 }} />}
      </div>

      {/* Content */}
      <div style={{ flex: 1, paddingBottom: 20 }}>
        <div
          style={{ background: 'var(--navy-card)', border: `1px solid ${status === 'Sent' ? 'rgba(74, 124, 89,0.2)' : 'var(--navy-border)'}`, borderRadius: 12, overflow: 'hidden', cursor: 'pointer' }}
          onClick={() => setExpanded(!expanded)}
        >
          <div style={{ padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <span style={{ fontSize: 18 }}>{template.icon}</span>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--white)' }}>Step {template.step}: {template.label}</div>
                <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>via {template.channel}</div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <span style={{ fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 12, background: `${sc}20`, color: sc }}>{status}</span>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--gray-500)" strokeWidth="2" style={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
                <polyline points="6 9 12 15 18 9"/>
              </svg>
            </div>
          </div>
          {expanded && (
            <div style={{ borderTop: '1px solid var(--navy-border)', padding: '16px 20px', background: 'rgba(26, 23, 20, 0.02)' }}>
              {template.subject && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 11, color: 'var(--gray-500)', fontWeight: 600, marginBottom: 4 }}>SUBJECT</div>
                  <div style={{ fontSize: 13, color: 'var(--gray-100)', padding: '8px 12px', background: 'var(--navy)', borderRadius: 6 }}>
                    {fillTemplate(template.subject)}
                  </div>
                </div>
              )}
              <div>
                <div style={{ fontSize: 11, color: 'var(--gray-500)', fontWeight: 600, marginBottom: 4 }}>MESSAGE</div>
                <pre style={{ fontSize: 13, color: 'var(--gray-100)', padding: '12px 14px', background: 'var(--navy)', borderRadius: 6, whiteSpace: 'pre-wrap', fontFamily: 'inherit', lineHeight: 1.65 }}>
                  {fillTemplate(template.body)}
                </pre>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <button style={smallBtnStyle('#4A7C59')}>Mark as Sent</button>
                <button style={smallBtnStyle('var(--teal)')}>Copy to Clipboard</button>
                <button style={smallBtnStyle('var(--gray-500)')}>Edit Template</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Spec({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: 'var(--gray-500)', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--white)' }}>{value}</div>
    </div>
  );
}

function NumberCard({ label, value, color, bg, sub }) {
  return (
    <div style={{ background: bg, border: '1px solid var(--navy-border)', borderRadius: 10, padding: '16px 20px' }}>
      <div style={{ fontSize: 11, color: 'var(--gray-500)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color, marginBottom: 4 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>{sub}</div>}
    </div>
  );
}

function ReturnRow({ label, value, color, bold }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid rgba(232, 227, 218, 0.7)' }}>
      <span style={{ fontSize: 12, color: 'var(--gray-500)' }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: bold ? 700 : 500, color: color || 'var(--gray-300)' }}>{value}</span>
    </div>
  );
}

function actionBtnStyle(color) {
  return { padding: '10px 20px', borderRadius: 8, border: `1px solid ${color}40`, background: `${color}15`, color, fontSize: 13, fontWeight: 600, cursor: 'pointer' };
}

function tabBtnStyle(active) {
  return { padding: '8px 16px', borderRadius: 8, border: active ? '1px solid var(--teal)' : '1px solid var(--navy-border)', background: active ? 'var(--teal-dim)' : 'transparent', color: active ? 'var(--teal)' : 'var(--gray-500)', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
}

function smallBtnStyle(color) {
  return { padding: '5px 12px', borderRadius: 6, border: `1px solid ${color}40`, background: `${color}12`, color, fontSize: 11, fontWeight: 600, cursor: 'pointer' };
}

function LoadingScreen() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', flexDirection: 'column', gap: 16 }}>
      <div style={{ width: 48, height: 48, border: '3px solid var(--navy-border)', borderTopColor: 'var(--teal)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      <div style={{ color: 'var(--gray-500)', fontSize: 14 }}>Building deal packet...</div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
